import React, { useState, useEffect } from 'react';
import { FileText, Download, RefreshCw, PlusCircle } from 'lucide-react';
import { managementReportApi, ManagementReport } from '../api/managementReport.api';
import { useAuth } from '@contexts/AuthContext';

const ManagementReportPage: React.FC = () => {
  const { user } = useAuth();
  const [reports, setReports] = useState<ManagementReport[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [params, setParams] = useState({ include_risks: true, include_policies: true, include_capa: true, include_tasks: true });

  const companyId = user?.company_id;

  const load = async () => {
    setIsLoading(true);
    setError('');
    try {
      const list = await managementReportApi.list(companyId ?? undefined);
      setReports(list);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { load(); }, [companyId]);

  const handleGenerate = async () => {
    if (!companyId) { setError('No company ID found.'); return; }
    setGenerating(true);
    setError('');
    setSuccess('');
    try {
      await managementReportApi.generate({ company_id: companyId, parameters: params });
      setSuccess('Report generated successfully.');
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Management Reports</h1>
        <button onClick={load} className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">
          <RefreshCw size={16} />
        </button>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}
      {success && <div className="p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">{success}</div>}

      {/* Generate Card */}
      <div className="bg-white border rounded-xl p-6 space-y-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <PlusCircle size={20} className="text-blue-600" /> Generate New Report
        </h2>
        <p className="text-sm text-gray-600">Generate a comprehensive PDF report combining all compliance data for management review.</p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { key: 'include_risks', label: 'Risk Register' },
            { key: 'include_policies', label: 'Policy Status' },
            { key: 'include_capa', label: 'CAPA Summary' },
            { key: 'include_tasks', label: 'Task Overview' },
          ].map(({ key, label }) => (
            <label key={key} className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
              <input type="checkbox" checked={params[key as keyof typeof params]}
                onChange={e => setParams(p => ({ ...p, [key]: e.target.checked }))}
                className="w-4 h-4 accent-blue-600" />
              <span className="text-sm text-gray-700">{label}</span>
            </label>
          ))}
        </div>

        <button onClick={handleGenerate} disabled={generating}
          className="flex items-center gap-2 px-5 py-2.5 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
          {generating ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              Generating PDF...
            </>
          ) : (
            <><FileText size={16} /> Generate Report</>
          )}
        </button>
      </div>

      {/* Recent Reports */}
      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b bg-gray-50">
          <h2 className="text-sm font-semibold text-gray-700">Recent Reports</h2>
        </div>
        {isLoading ? (
          <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" /></div>
        ) : reports.length === 0 ? (
          <div className="py-12 text-gray-500">No reports generated yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>{['Generated By', 'Date', 'Contents', 'Download'].map(h => (
                <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y">
              {reports.map(r => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-left text-gray-900">{r.generated_by_name || `User #${r.generated_by}`}</td>
                  <td className="px-6 py-4 text-left text-gray-600">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="px-6 py-4 text-left">
                    <div className="flex flex-wrap gap-1">
                      {r.parameters && Object.entries(r.parameters).filter(([, v]) => v).map(([k]) => (
                        <span key={k} className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700">
                          {k.replace('include_', '')}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-left">
                    <button onClick={() => managementReportApi.downloadReport(r.id)}
                      className="flex items-center gap-1 text-blue-600 hover:underline text-sm">
                      <Download size={14} /> PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ManagementReportPage;
